/**
 * @fileoverview Test Supabase Auth Button Component
 * @active Development-only feature for testing Supabase Auth
 * 
 * This component provides a UI toggle for enabling/disabling Supabase Auth
 * during development. It only appears in development mode and allows
 * developers to test auth flows without affecting normal development.
 * 
 * @see {@link src/app/utils/supabase/client.ts} - Client-side localStorage state management
 * @see {@link src/app/utils/supabase/middleware.ts} - Middleware that checks cookies
 */

"use client";

import { useState, useEffect } from "react";
import { Box, Flex, Text, useMediaQuery } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { CheckIcon } from "@chakra-ui/icons";
import { 
  isSupabaseAuthTestEnabled, 
  setSupabaseAuthTestEnabled 
} from "@/app/utils/supabase/client";

// Subtle shine effect animation (same as WaitlistCTANavButton)
const shine = keyframes`
  0% { background-position: -100px; }
  60% { background-position: 250px; }
  100% { background-position: 250px; }
`;

// Pulse animation for the button (same as WaitlistCTANavButton)
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

interface TestSupabaseAuthButtonProps {
  /** Whether to use mobile styling */
  isMobile?: boolean;
}

/**
 * Development-only button for toggling Supabase Auth testing
 * Styled with TanStack-inspired colors (blues, oranges)
 * 
 * @component
 * @param {TestSupabaseAuthButtonProps} props - Component props
 * @returns {JSX.Element | null} Animated button or null if not in development mode
 */
const TestSupabaseAuthButton = ({ isMobile = false }: TestSupabaseAuthButtonProps) => {
  const [isActive, setIsActive] = useState(false);
  const [isCompactView] = useMediaQuery("(max-width: 1100px)");
  const [isDevelopment, setIsDevelopment] = useState(false);
  
  // Initialize state from localStorage on mount
  useEffect(() => {
    // Check if we're in development mode
    const devMode = process.env.NODE_ENV === "development";
    
    setIsDevelopment(devMode);
    
    // Only check localStorage in development mode
    if (devMode) {
      // Get initial state from localStorage
      setIsActive(isSupabaseAuthTestEnabled());
      
      // Also set a cookie for server-side middleware
      document.cookie = `supabase_auth_test_enabled=${isSupabaseAuthTestEnabled() ? 'true' : 'false'}; path=/; max-age=86400`;
    }
  }, []);
  
  // Don't render anything if not in development mode
  if (!isDevelopment) return null;
  
  const handleToggle = () => {
    const newState = !isActive;
    setIsActive(newState);
    setSupabaseAuthTestEnabled(newState);
    
    // Also set a cookie for server-side middleware
    document.cookie = `supabase_auth_test_enabled=${newState ? 'true' : 'false'}; path=/; max-age=86400`;
  };
  
  // Display for mobile navigation drawer
  if (isMobile) {
    return (
      <Flex 
        direction="column" 
        align="center" 
        w="full" 
        py={2}
      >
        <Box
          onClick={handleToggle}
          cursor="pointer"
          bgGradient={isActive 
            ? "linear(135deg, #38BDF8, #0EA5E9, #0284C7)" 
            : "linear(135deg, #FB923C, #F97316, #EA580C)"}
          borderRadius="full"
          px={5}
          py={2}
          color="white"
          fontWeight="600"
          fontSize="14px"
          boxShadow={isActive 
            ? "0 2px 10px rgba(56, 189, 248, 0.3)" 
            : "0 2px 10px rgba(249, 115, 22, 0.3)"}
          position="relative"
          overflow="hidden"
          transition="all 0.2s ease"
          _hover={{
            boxShadow: isActive 
              ? "0 4px 15px rgba(56, 189, 248, 0.5)" 
              : "0 4px 15px rgba(249, 115, 22, 0.5)",
            transform: "translateY(-1px)"
          }}
          sx={{
            animation: `${pulse} 3s infinite ease-in-out`
          }}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
            backgroundSize: '200px 100%',
            animation: `${shine} 3s infinite linear`,
          }}
        >
          <Flex direction="column" align="center">
            <Flex align="center" gap={2}>
              <Text>Test Supabase Auth</Text>
              {isActive && <CheckIcon color="green.300" />}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    );
  }

  // Handle compact layout for narrower screens
  if (isCompactView) {
    return (
      <Box
        onClick={handleToggle}
        cursor="pointer"
        bgGradient={isActive 
          ? "linear(to-r, #38BDF8, #0EA5E9)" 
          : "linear(to-r, #FB923C, #F97316)"}
        px={{ base: 3, md: 3.5 }}
        py="6px"
        h="30px"
        borderRadius="md"
        color="white"
        fontWeight="600"
        fontSize="13px"
        boxShadow={isActive 
          ? "0 2px 8px rgba(56, 189, 248, 0.15)" 
          : "0 2px 8px rgba(249, 115, 22, 0.15)"}
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        transition="all 0.2s ease"
        minW="auto"
        maxW="100%"
        whiteSpace="nowrap"
        _hover={{
          boxShadow: isActive 
            ? "0 4px 12px rgba(56, 189, 248, 0.3)" 
            : "0 4px 12px rgba(249, 115, 22, 0.3)",
          transform: "translateY(-1px)"
        }}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
          backgroundSize: '200px 100%',
          animation: `${shine} 3s infinite linear`,
        }}
      >
        <Flex align="center" gap={1}>
          <Text lineHeight="1">Test Auth</Text>
          {isActive && <CheckIcon color="green.300" />}
        </Flex>
      </Box>
    );
  }

  // Default display for desktop header
  return (
    <Box
      onClick={handleToggle}
      cursor="pointer"
      bgGradient={isActive 
        ? "linear(to-r, #38BDF8, #0EA5E9)" 
        : "linear(to-r, #FB923C, #F97316)"}
      px={{ base: 3, md: 4 }}
      py="6px"
      h="30px"
      borderRadius="md"
      color="white"
      fontWeight="600"
      fontSize="13px"
      boxShadow={isActive 
        ? "0 2px 8px rgba(56, 189, 248, 0.15)" 
        : "0 2px 8px rgba(249, 115, 22, 0.15)"}
      position="relative"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
      transition="all 0.2s ease"
      minW="auto"
      maxW="100%"
      whiteSpace="nowrap"
      flexShrink={0}
      _hover={{
        boxShadow: isActive 
          ? "0 4px 12px rgba(56, 189, 248, 0.3)" 
          : "0 4px 12px rgba(249, 115, 22, 0.3)",
        transform: "translateY(-1px)"
      }}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
        backgroundSize: '200px 100%',
        animation: `${shine} 3s infinite linear`,
      }}
      mr={2}
    >
      <Flex align="center" gap={1}>
        <Text lineHeight="1">Test Supabase Auth</Text>
        {isActive && <CheckIcon color="green.300" />}
      </Flex>
    </Box>
  );
};

export default TestSupabaseAuthButton;
